import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { snapshotInvoiceCost } from '@/lib/invoiceCostSnapshot'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatDate } from '@/lib/utils'
import { formatAddress, mapsUrl, fromAddressPrefix } from '@/lib/address'
import NewJobModal from '@/components/NewJobModal'
import { JobPhotoStrip } from '@/components/JobPhotoStrip'
import { JobPriorityCorner, JobPriorityChip } from '@/components/JobPriorityIndicator'
import { notifyPortal } from '@/lib/portalNotify'
import {
  buildStatusTransitionPayload,
  NEXT_STATUS,
  HOLD_STATUSES,
  canEnterHold,
  resumeTarget,
  type FieldJobStatus,
} from '@/pages/field/jobStatusTransition'
import {
  projectFieldJobLineItemsToInvoiceJsonb,
  fetchContactEmail,
  type FieldJobLineItem,
} from '@/lib/jobInvoiceConvert'
import {
  Plus, X, Truck, MapPin, Clock, CheckCircle2, Phone, User,
  Calendar, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Check, DollarSign, Navigation,
  ExternalLink, Search, Edit2, FileText, Building2, Wrench, Maximize2,
  AlertTriangle, Repeat, Pause, Play,
} from 'lucide-react'
import { detectConflictsInList } from '@/lib/scheduleConflicts'
import { keepRecurringWindow } from '@/lib/keepRecurringWindow'
import { customerLabelForJob } from '@/lib/customerDisplay'
import { STAFF_EVENT_COPY, type StaffLifecycleEvent as VendorLifecycleEvent } from '@/lib/vendorLifecycleCopy'
import { useCalendarLabelPrefs, CALENDAR_LABEL_DEFAULTS, type CalendarLabelPrefs } from '@/lib/useCalendarLabelPrefs'
import CalendarLabelToggles from '@/pages/field/CalendarLabelToggles'
import { toast } from 'sonner'
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  addDays, addWeeks, addMonths, subWeeks, subMonths, subDays,
  isSameMonth, isToday, parseISO,
} from 'date-fns'

// Active status palette is non-theming (operator muscle memory — see
// tokens.css --status-job-* family). cfg.color resolves at runtime to the
// underlying var(--status-job-X) string, so call sites that string-concat
// alpha suffixes (like `${cfg.color}90`) MUST be migrated to color-mix()
// expressions — 'var(--status-job-X)NN' is invalid CSS.
//
// Complete is the one exception: --status-job-complete + bg + border theme
// per mode (alpha-white in dark, alpha-black in light, alpha-warm-brown in
// sepia) because completed jobs are the dominant visual category on a
// working calendar and alpha-white-everywhere goes invisible on light/sepia
// canvases.
export const JOB_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  scheduled: { label: 'Scheduled', color: 'var(--status-job-scheduled)',
    bg: 'color-mix(in srgb, var(--status-job-scheduled) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-scheduled) var(--status-tint-edge), transparent)' },
  en_route:  { label: 'En route',  color: 'var(--status-job-en-route)',
    bg: 'color-mix(in srgb, var(--status-job-en-route) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-en-route) var(--status-tint-edge), transparent)' },
  on_site:   { label: 'On site',   color: 'var(--status-job-on-site)',
    bg: 'color-mix(in srgb, var(--status-job-on-site) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-on-site) var(--status-tint-edge), transparent)' },
  complete:  { label: 'Complete',  color: 'var(--status-job-complete)',
    bg: 'var(--status-job-complete-bg)',
    border: 'var(--status-job-complete-border)' },
  cancelled: { label: 'Cancelled', color: 'var(--status-job-cancelled)',
    bg: 'color-mix(in srgb, var(--status-job-cancelled) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-cancelled) var(--status-tint-edge), transparent)' },
  on_customer_hold: { label: 'Customer hold', color: 'var(--status-job-customer-hold)',
    bg: 'color-mix(in srgb, var(--status-job-customer-hold) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-customer-hold) var(--status-tint-edge), transparent)' },
  on_vendor_hold: { label: 'Vendor hold', color: 'var(--status-job-vendor-hold)',
    bg: 'color-mix(in srgb, var(--status-job-vendor-hold) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-vendor-hold) var(--status-tint-edge), transparent)' },
  pending_parts: { label: 'Pending parts', color: 'var(--status-job-pending-parts)',
    bg: 'color-mix(in srgb, var(--status-job-pending-parts) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-pending-parts) var(--status-tint-edge), transparent)' },
  awaiting_approval: { label: 'Awaiting approval', color: 'var(--status-job-awaiting-approval)',
    bg: 'color-mix(in srgb, var(--status-job-awaiting-approval) var(--status-tint-fill), transparent)',
    border: 'color-mix(in srgb, var(--status-job-awaiting-approval) var(--status-tint-edge), transparent)' },
}

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7)

type Tab = 'calendar' | 'board' | 'map' | 'crew'


// ─── Job Detail Drawer ────────────────────────────────────────────────────────
function JobDrawer({ job, onClose, onEdit, onUpdate, agents }: any) {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const [checklist, setChecklist] = useState<{ id: string; text: string; done: boolean }[]>(job.checklist || [])
  const [newCheckItem, setNewCheckItem] = useState('')
  const [notes, setNotes] = useState(job.job_notes || '')
  const [woNumber, setWoNumber] = useState(job.work_order_number || '')
  const [poNumber, setPoNumber] = useState(job.purchase_order_number || '')
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [holdMenuOpen, setHoldMenuOpen] = useState(false)

  useEffect(() => {
    setChecklist(job.checklist || [])
  }, [job.id, job.checklist])

  useEffect(() => {
    setNotes(job.job_notes || '')
  }, [job.id, job.job_notes])

  useEffect(() => {
    setWoNumber(job.work_order_number || '')
    setPoNumber(job.purchase_order_number || '')
  }, [job.id, job.work_order_number, job.purchase_order_number])

  const handleGenerateInvoice = async () => {
    if (!workspace?.id || generatingInvoice) return
    setGeneratingInvoice(true)
    // Three parallel reads: invoice count for the number, line items, and
    // the contact's email (job rows don't carry the email, but the invoice
    // needs it so Send/receipt-email can light up — fetchContactEmail is
    // the canonical lookup shared with JobDetail + BillingTab).
    // invoice_number assigned by the trigger (Phase A, race-safe), so the
    // invoice-count query is no longer needed.
    const [{ data: lineRows, error: lineErr }, contactEmail] = await Promise.all([
      supabase.from('field_job_line_items').select('*').eq('field_job_id', job.id).order('sort_index', { ascending: true }),
      fetchContactEmail(job.contact_id),
    ])
    if (lineErr) {
      setGeneratingInvoice(false)
      toast.error(`Failed to load line items: ${lineErr.message}`)
      return
    }
    const projectedLineItems = projectFieldJobLineItemsToInvoiceJsonb((lineRows ?? []) as FieldJobLineItem[])

    const { data: invoice, error } = await supabase.from('invoices').insert({
      workspace_id: workspace.id,
      contact_id: job.contact_id,
      contact_name: job.contact_name,
      contact_email: contactEmail,
      company_name: job.company_name || null,
      status: 'draft',
      line_items: projectedLineItems,
      subtotal: job.subtotal ?? 0,
      tax_rate: job.tax_rate ?? 0,
      tax_amount: job.tax_amount ?? 0,
      total: job.total ?? 0,
      amount_due: job.total ?? 0,
      amount_paid: 0,
      discount_type: job.discount_type ?? 'none',
      discount_value: job.discount_value ?? 0,
      discount_amount: job.discount_amount ?? 0,
      currency: job.currency ?? 'USD',
      issue_date: job.scheduled_date || new Date().toISOString().split('T')[0],
      notes: job.job_notes || job.title,
      // Inherit from the source job; frozen at creation per attribution
      // snapshot precedent (see invoices.service_line_id column comment).
      service_line_id: job.service_line_id ?? null,
    }).select().single()
    setGeneratingInvoice(false)
    if (error || !invoice) {
      toast.error(`Error: ${error?.message || 'Failed to create invoice'}`)
      return
    }
    // Guarded link: only attach the invoice if the job isn't already invoiced (race/stale-cache guard).
    const { data: linkedRows, error: linkErr } = await supabase
      .from('field_jobs')
      .update({ invoice_id: invoice.id })
      .eq('id', job.id)
      .is('invoice_id', null)
      .select('id')
    if (linkErr) {
      // Link failed: void the orphaned invoice we just created, surface the error.
      const { error: voidErr } = await supabase.from('invoices').update({ status: 'void' }).eq('id', invoice.id)
      if (voidErr) {
        toast.error(`Failed to link invoice, and a draft invoice (${invoice.invoice_number ?? invoice.id}) could not be auto-voided — please void it manually.`)
      } else {
        toast.error(`Failed to link invoice: ${linkErr.message}`)
      }
      return
    }
    if (!linkedRows || linkedRows.length === 0) {
      // Job was already invoiced between load and click — void the orphan, don't double-bill.
      const { error: voidErr } = await supabase.from('invoices').update({ status: 'void' }).eq('id', invoice.id)
      if (voidErr) {
        toast.warning(`This visit was already invoiced. A draft invoice (${invoice.invoice_number ?? invoice.id}) was created but could not be auto-voided — please void it manually.`)
      } else {
        toast.warning('This visit was already invoiced — no second invoice was created.')
      }
      return
    }
    // Slice 4: freeze the job's cost basis onto the new invoice (staff-only,
    // non-fatal — never throws, so a snapshot failure can't break the convert).
    await snapshotInvoiceCost(supabase, workspace.id, invoice.id, [job.id])
    onUpdate(job.id, { invoice_id: invoice.id })
    toast.success('Invoice created')
    navigate(`/app/invoices/${invoice.id}`)
  }

  const handleStatusAdvance = () => {
    const next = NEXT_STATUS[job.status]
    if (!next) return
    onUpdate(job.id, buildStatusTransitionPayload(job.status, next))
    toast.success(`Marked ${JOB_STATUS_CONFIG[next]?.label}`)
    if (next === 'complete') notifyPortal({ event: 'job_completed', job_id: job.id })
  }

  const handleSendEnRoute = () => {
    onUpdate(job.id, buildStatusTransitionPayload(job.status, 'en_route'))
    toast.success('Marked en route')
  }

  const handleEnterHold = (holdStatus: FieldJobStatus) => {
    onUpdate(job.id, buildStatusTransitionPayload(job.status, holdStatus))
    setHoldMenuOpen(false)
    toast.success(`Paused — ${JOB_STATUS_CONFIG[holdStatus]?.label}`)
  }

  const handleResume = () => {
    const target = resumeTarget()
    onUpdate(job.id, buildStatusTransitionPayload(job.status, target))
    toast.success(`Resumed — ${JOB_STATUS_CONFIG[target]?.label}`)
  }

  const addCheckItem = () => {
    if (!newCheckItem.trim()) return
    const updated = [...checklist, { id: Date.now().toString(), text: newCheckItem.trim(), done: false }]
    setChecklist(updated)
    setNewCheckItem('')
    onUpdate(job.id, { checklist: updated, checklist_complete: updated.every(c => c.done) })
  }

  const toggleCheckItem = (itemId: string) => {
    const updated = checklist.map(c => c.id === itemId ? { ...c, done: !c.done } : c)
    setChecklist(updated)
    onUpdate(job.id, { checklist: updated, checklist_complete: updated.every(c => c.done) })
  }

  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
  const nextStatus = NEXT_STATUS[job.status]
  const nextCfg = nextStatus ? JOB_STATUS_CONFIG[nextStatus] : null
  const doneCount = checklist.filter(c => c.done).length

  return (
    <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(380px, 100vw)', background: 'var(--surface-raised)', borderLeft: '1px solid var(--border-subtle)', zIndex: 150, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => navigate(`/app/jobs/${job.id}`)} aria-label="Open full view" data-testid="field-job-open-fullview-cta"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
              <Maximize2 style={{ width: 14, height: 14 }} />
            </button>
            {onEdit && (
              <button onClick={onEdit} aria-label="Edit job" data-testid="field-job-edit-cta"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                <Edit2 style={{ width: 14, height: 14 }} />
              </button>
            )}
            <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>{job.title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>
          {job.service_line?.name && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
              {job.service_line.name}
            </span>
          )}
          <JobPriorityChip priority={job.priority} size="sm" />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Customer</p>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{job.contact_name}</p>
          {job.contact_phone && (
            <a href={`tel:${job.contact_phone}`} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
              <Phone style={{ width: 12, height: 12 }} /> {job.contact_phone}
            </a>
          )}
        </div>

        {/* External references — WO/PO numbers handed over by clients. Same
            local-state + onBlur write pattern as job_notes (textarea below).
            Inputs render even when empty so the operator can add a number
            from the drawer without opening the full edit modal. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>References</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 4 }}>WO #</label>
              <input value={woNumber} onChange={e => setWoNumber(e.target.value)}
                onBlur={() => onUpdate(job.id, { work_order_number: woNumber || null })}
                placeholder="WO-12345"
                style={{ width: '100%', padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 4 }}>PO #</label>
              <input value={poNumber} onChange={e => setPoNumber(e.target.value)}
                onBlur={() => onUpdate(job.id, { purchase_order_number: poNumber || null })}
                placeholder="PO-4471"
                style={{ width: '100%', padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>

        {/* Photos strip — compact horizontal layout for the drawer. Shares
            the upload/lightbox internals with JobDetail's grid via the
            JobPhotoStrip module. Hidden until workspace is loaded so the
            upload helper never sees an undefined workspaceId. */}
        {workspace?.id && (
          <JobPhotoStrip jobId={job.id} workspaceId={workspace.id} />
        )}

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Schedule & Location</p>
          {job.scheduled_date && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <Calendar style={{ width: 13, height: 13, color: 'var(--accent)', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{formatDate(job.scheduled_date, 'EEEE, MMM d')}</span>
            </div>
          )}
          {job.scheduled_start && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <Clock style={{ width: 13, height: 13, color: 'var(--accent)', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{job.scheduled_start}{job.scheduled_end ? ` – ${job.scheduled_end}` : ''}</span>
            </div>
          )}
          {job.location?.company?.name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <Building2 style={{ width: 13, height: 13, color: 'var(--accent)', flexShrink: 0 }} />
              <button onClick={() => navigate(`/app/companies/${job.location.company.id}`)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-text)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-primary)'}>
                {job.location.company.name}
              </button>
            </div>
          )}
          {job.location?.name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <Building2 style={{ width: 13, height: 13, color: 'var(--positive)', flexShrink: 0 }} />
              <button onClick={() => job.location.company_id && navigate(`/app/companies/${job.location.company_id}`)}
                disabled={!job.location.company_id}
                style={{ background: 'none', border: 'none', padding: 0, cursor: job.location.company_id ? 'pointer' : 'default', fontSize: 13, color: 'var(--ink-secondary)', textAlign: 'left' }}
                onMouseEnter={e => { if (job.location.company_id) e.currentTarget.style.color = 'var(--positive)' }}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-secondary)'}>
                {job.location.name}
              </button>
            </div>
          )}
          {job.vendor?.name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <Wrench style={{ width: 13, height: 13, color: 'var(--warning)', flexShrink: 0 }} />
              <button onClick={() => navigate(`/app/vendors/${job.vendor.id}`)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--warning)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-primary)'}>
                {job.vendor.name}
              </button>
            </div>
          )}
          {(job.address_line1 || job.address_city) && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <MapPin style={{ width: 13, height: 13, color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                {job.address_line1 && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>{job.address_line1}</p>}
                {job.address_city && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{job.address_city}{job.address_state ? `, ${job.address_state}` : ''}</p>}
              </div>
              <a href={mapsUrl(fromAddressPrefix(job)) ?? '#'}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
                <Navigation style={{ width: 11, height: 11 }} /> Maps
              </a>
            </div>
          )}
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Technician</p>
          {job.assigned_agent_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent-ink)' }}>
                {job.assigned_agent_name.charAt(0).toUpperCase()}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{job.assigned_agent_name}</span>
            </div>
          )}
          <select value={job.assigned_agent_id || ''} onChange={e => {
            const a = agents.find((a: any) => a.id === e.target.value)
            onUpdate(job.id, {
              assigned_agent_id: e.target.value || null,
              assigned_agent_name: a?.full_name || null,
            })
            toast.success(a ? `Assigned to ${a.full_name}` : 'Unassigned')
          }}
            style={{ width: '100%', padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
            <option value="">Unassigned</option>
            {agents.map((a: any) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
          {/* CSR — internal coordinator, distinct from the field tech. No
              denormalized name; we resolve from the agents list at render. */}
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '10px 0 6px' }}>CSR</p>
          <select value={job.csr_id || ''} onChange={e => {
            const a = agents.find((a: any) => a.id === e.target.value)
            onUpdate(job.id, { csr_id: e.target.value || null })
            toast.success(a ? `CSR: ${a.full_name}` : 'CSR cleared')
          }}
            style={{ width: '100%', padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
            <option value="">None</option>
            {agents.map((a: any) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
              Checklist {checklist.length > 0 && <span style={{ color: 'var(--accent)' }}>{doneCount}/{checklist.length}</span>}
            </p>
            {checklist.length > 0 && (
              <div style={{ width: 60, height: 4, background: 'var(--border-subtle)', borderRadius: 100, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(doneCount / checklist.length) * 100}%`, background: 'var(--positive)', borderRadius: 100 }} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {checklist.map(item => (
              <div key={item.id} onClick={() => toggleCheckItem(item.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', transition: 'background 150ms ease' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: 16, height: 16, borderRadius: 5, border: `2px solid ${item.done ? 'var(--positive)' : 'var(--ink-tertiary)'}`, background: item.done ? 'var(--positive-dim)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 150ms ease' }}>
                  {item.done && <Check style={{ width: 10, height: 10, color: 'var(--positive)' }} />}
                </div>
                <span style={{ fontSize: 12, color: item.done ? 'var(--ink-tertiary)' : 'var(--ink-primary)', textDecoration: item.done ? 'line-through' : 'none', flex: 1 }}>{item.text}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addCheckItem() }}
              placeholder="Add checklist item..."
              style={{ flex: 1, padding: '6px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }} />
            <button onClick={addCheckItem} style={{ padding: '6px 10px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600 }}>Add</button>
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Job notes</p>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => onUpdate(job.id, { job_notes: notes })}
            placeholder="Notes, instructions, access codes..."
            rows={3}
            style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--ink-secondary)', resize: 'none', lineHeight: 1.6, boxSizing: 'border-box' }} />
        </div>
      </div>

      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Held jobs swap the forward-advance affordance for Resume → Scheduled.
            Holds pause the live footprint, so the en_route / next-status buttons
            do not appear until the operator resumes the job. */}
        {HOLD_STATUSES.includes(job.status) ? (
          <button onClick={handleResume}
            style={{ width: '100%', padding: '11px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, color: 'var(--accent-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <Play style={{ width: 14, height: 14 }} /> Resume → Scheduled
          </button>
        ) : (
          <>
            {job.status === 'scheduled' && (
              <button onClick={handleSendEnRoute}
                style={{ width: '100%', padding: '10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, color: 'var(--accent-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                <Navigation style={{ width: 14, height: 14 }} /> Mark en route
              </button>
            )}
            {nextCfg && (
              <button onClick={handleStatusAdvance}
                style={{ width: '100%', padding: '11px', background: `color-mix(in srgb, ${nextCfg.color} var(--status-tint-dim), transparent)`, border: `1px solid ${nextCfg.border}`, borderRadius: 10, color: nextCfg.color, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                → Mark {nextCfg.label}
              </button>
            )}
          </>
        )}
        {/* Hold menu — secondary affordance, only on non-terminal jobs that
            aren't already held. Toggle reveals the 4 hold choices inline. */}
        {canEnterHold(job.status) && !HOLD_STATUSES.includes(job.status) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={() => setHoldMenuOpen(o => !o)}
              style={{ width: '100%', padding: '8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Pause style={{ width: 12, height: 12 }} /> {holdMenuOpen ? 'Cancel' : 'Hold…'}
            </button>
            {holdMenuOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '4px 0' }}>
                {HOLD_STATUSES.map(h => {
                  const hc = JOB_STATUS_CONFIG[h]
                  return (
                    <button key={h} onClick={() => handleEnterHold(h)}
                      style={{ width: '100%', padding: '7px 10px', background: hc.bg, border: `1px solid ${hc.border}`, borderRadius: 8, color: hc.color, fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                      {hc.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {job.status === 'complete' && !job.invoice_id && (
          <button onClick={handleGenerateInvoice} disabled={generatingInvoice} data-testid="field-job-generate-invoice"
            style={{ width: '100%', padding: '11px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 10, color: 'var(--positive)', fontSize: 13, fontWeight: 700, cursor: generatingInvoice ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: generatingInvoice ? 0.6 : 1 }}>
            <FileText style={{ width: 14, height: 14 }} /> {generatingInvoice ? 'Creating...' : 'Generate invoice'}
          </button>
        )}
        {job.invoice_id && (
          <button onClick={() => navigate(`/app/invoices/${job.invoice_id}`)} data-testid="field-job-view-invoice"
            style={{ width: '100%', padding: '11px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--ink-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <ExternalLink style={{ width: 14, height: 14 }} /> View invoice
          </button>
        )}
        {/* Phase 4 — Field Hub gap-fills (see "Field Hub gap-fills" Schema follow-up).
            Original button flipped payment_collected boolean without creating an invoice;
            Phase 4 wires this to job→invoice flow and restores the button (with real
            invoice INSERT replacing the boolean-only update). */}
        {false && job.status === 'complete' && !job.payment_collected && (
          <button onClick={() => { onUpdate(job.id, { payment_collected: true }); toast.success('Payment collected!') }}
            style={{ width: '100%', padding: '11px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 10, color: 'var(--positive)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <DollarSign style={{ width: 14, height: 14 }} /> Collect payment
          </button>
        )}
        {/* Phase 4 — Field Hub gap-fills (see "Field Hub gap-fills" Schema follow-up).
            Badge displays "Payment collected" when payment_collected=true; hidden during
            soft-launch alongside the button (item 6 hide). Phase 4 reverts both together
            when the real job→invoice flow lands and the boolean reflects honest state. */}
        {false && job.payment_collected && (
          <div style={{ textAlign: 'center', padding: '8px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-faint), transparent)', borderRadius: 10, border: '1px solid var(--positive-dim)' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--positive)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <CheckCircle2 style={{ width: 13, height: 13 }} /> Payment collected
            </span>
          </div>
        )}
      </div>
    </motion.div>
  )
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────
// Inline conflict marker used by every job-card surface (calendar pills,
// week/day clusters, list rows, crew rows, map list cards). One source of
// truth for the visual; consumers just check job._hasConflict before
// rendering. `size` lets compact surfaces request a smaller icon.
function ConflictIndicator({ size = 12 }: { size?: number }) {
  return (
    <span title="Schedule conflict — overlapping job at this time" aria-label="Schedule conflict"
      style={{ display: 'inline-flex', flexShrink: 0 }}>
      <AlertTriangle style={{ width: size, height: size, color: 'var(--conflict-indicator)' }} />
    </span>
  )
}

// Sibling of ConflictIndicator — marks materialized recurring-series
// occurrences. Quiet `--ink-secondary` color so it reads as informational,
// not a warning, and visually coexists with ConflictIndicator (which is
// lime — see --conflict-indicator in tokens.css; was --danger before the
// palette audit closed the cancelled-vs-conflict red-on-red overlap) when
// a job is both recurring AND conflicted. Consumers check `job.series_id`
// directly (the FK from Chunk 1).
function RecurringIndicator({ size = 12 }: { size?: number }) {
  return (
    <span title="Recurring — part of a series" aria-label="Recurring job"
      style={{ display: 'inline-flex', flexShrink: 0 }}>
      <Repeat style={{ width: size, height: size, color: 'var(--ink-secondary)' }} />
    </span>
  )
}

// Sibling of ConflictIndicator + RecurringIndicator — marks the vendor's
// latest lifecycle event (en_route, on_site, delayed, return_visit_
// scheduled, completed) sourced from V2c's vendor_assignment_events.
// Visual is a single small colored dot using the shared
// STAFF_EVENT_COPY palette; the full state label + relative time +
// optional note ride on the tooltip. Kept compact on purpose — even at
// MonthView's narrowest pill the indicator never collides with the
// existing two indicators.
function VendorStateIndicator({ state, size = 10 }: {
  state: { event_type: VendorLifecycleEvent; created_at: string; note: string | null }
  size?: number
}) {
  const c = STAFF_EVENT_COPY[state.event_type]
  const rel = (() => {
    try {
      const ms = Date.now() - new Date(state.created_at).getTime()
      if (ms < 60_000) return 'just now'
      if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
      if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
      return `${Math.floor(ms / 86_400_000)}d ago`
    } catch { return '' }
  })()
  const title = `Vendor: ${c.label}${rel ? ' · ' + rel : ''}${state.note ? ' · ' + state.note : ''}`
  return (
    <span title={title} aria-label={`Vendor ${c.label}`}
      style={{ display: 'inline-flex', flexShrink: 0, width: size, height: size, borderRadius: '50%', background: c.color, border: '1px solid var(--surface)' }} />
  )
}

// Stacked multi-line label, one line per enabled toggle, in a fixed top-to-
// bottom order. Per docs/fieldhub-calendar-redesign-plan.md. Color always
// encodes status via JOB_STATUS_CONFIG; the status_text toggle adds the
// word on top.
function CalendarJobPill({
  job, prefs = CALENDAR_LABEL_DEFAULTS, onClick,
}: {
  job: any
  prefs?: CalendarLabelPrefs
  onClick: () => void
}) {
  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled

  const customer = prefs.customer ? customerLabelForJob(job) : null
  const cityState = prefs.city_state ? composeCityState(job.address_city, job.address_state) : null
  const serviceType = prefs.service_type ? (job.service_line?.name || null) : null
  const assignee = prefs.assignee ? (job.assigned_agent_name || job.vendor?.name || null) : null
  const hasBadge = !!job._hasConflict || !!job.series_id || !!job._vendorLatest
  const hasAnyLine =
    (prefs.time && job.scheduled_start) ||
    !!customer ||
    (prefs.title && job.title) ||
    !!serviceType ||
    !!cityState ||
    !!assignee ||
    prefs.status_text

  return (
    <div onClick={e => { e.stopPropagation(); onClick() }}
      style={{ padding: '4px 7px', borderRadius: 6, background: cfg.bg, border: `1px solid ${cfg.border}`, cursor: 'pointer', marginBottom: 2, overflow: 'hidden', transition: 'opacity 150ms ease', position: 'relative' }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
      <JobPriorityCorner priority={job.priority} />
      {hasBadge && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: hasAnyLine ? 2 : 0 }}>
          {job._hasConflict && <ConflictIndicator size={10} />}
          {job.series_id && <RecurringIndicator size={10} />}
          {job._vendorLatest && <VendorStateIndicator state={job._vendorLatest} />}
        </div>
      )}
      {prefs.time && job.scheduled_start && (
        <p style={{ fontSize: 11, fontWeight: 500, color: cfg.color, opacity: 0.75, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {job.scheduled_start}
        </p>
      )}
      {customer && (
        <p style={{ fontSize: 12, fontWeight: 700, color: cfg.color, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customer}
        </p>
      )}
      {prefs.title && job.title && (
        <p style={{ fontSize: 11, fontWeight: 500, color: cfg.color, opacity: 0.85, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {job.title}
        </p>
      )}
      {serviceType && (
        <p style={{ fontSize: 11, fontWeight: 500, color: cfg.color, opacity: 0.7, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {serviceType}
        </p>
      )}
      {cityState && (
        <p style={{ fontSize: 11, fontWeight: 500, color: cfg.color, opacity: 0.65, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cityState}
        </p>
      )}
      {assignee && (
        <p style={{ fontSize: 11, fontWeight: 500, color: cfg.color, opacity: 0.7, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {assignee}
        </p>
      )}
      {prefs.status_text && (
        <p style={{ fontSize: 10, fontWeight: 600, color: cfg.color, opacity: 0.6, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
          {cfg.label}
        </p>
      )}
    </div>
  )
}

function composeCityState(city: string | null | undefined, state: string | null | undefined): string | null {
  const c = city?.trim()
  const s = state?.trim()
  if (c && s) return `${c}, ${s}`
  if (c) return c
  if (s) return s
  return null
}

function MonthView({ currentDate, jobs, onJobClick, onNewJob, isMobile }: any) {
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const startDate = startOfWeek(monthStart)
  const endDate = endOfWeek(monthEnd)
  const days = []
  let day = startDate
  while (day <= endDate) { days.push(day); day = addDays(day, 1) }

  const jobsByDate = useMemo(() => {
    const map: Record<string, any[]> = {}
    jobs.forEach((job: any) => {
      if (job.scheduled_date) {
        if (!map[job.scheduled_date]) map[job.scheduled_date] = []
        map[job.scheduled_date].push(job)
      }
    })
    return map
  }, [jobs])

  // Per-user label toggles (persisted in profiles.calendar_label_preferences)
  const { prefs } = useCalendarLabelPrefs()

  return (
    <div style={isMobile ? { display: 'flex', flexDirection: 'column' } : { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sub-header: status legend (left) + label-toggles gear (right). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {Object.values(JOB_STATUS_CONFIG).map(cfg => (
            <div key={cfg.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{cfg.label}</span>
            </div>
          ))}
        </div>
        <CalendarLabelToggles />
      </div>
      {/* repeat(7, minmax(0, 1fr)) (not bare 1fr) so columns can shrink below
          their content's min-content size — required because the pills carry
          whiteSpace:nowrap text whose intrinsic min-content would otherwise
          force the grid wider than the available space, desyncing from the
          body grid below. The pill's own overflow:hidden + text-overflow:
          ellipsis then engages cleanly inside the locked column. Header +
          body share the SAME minmax so they stay aligned by construction. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border-subtle)' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>{d}</div>
        ))}
      </div>
      {/* Scroll wrapper — sits between the (non-scrolling) sub-header +
          day-of-week row above and the grid below. flex:1 + overflow:auto
          gives the grid an indefinite block-size: minmax(120px, max-content)
          can now actually expand each row to its tallest cell's content
          (rather than being clamped by a flex-bounded grid track total).
          When the grid grows past viewport, THIS div scrolls vertically;
          the legend + day-of-week header stay put. On mobile the chain is
          dissolved so the page itself scrolls (AppLayout's <main>), and
          this wrapper becomes a natural-height block. */}
      <div style={isMobile ? {} : { flex: 1, overflow: 'auto' }}>
        {/* Body grid: minmax(0, 1fr) mirrors the header above so the day
            cells stay locked at 1/7 of the available width even when pill
            content has large min-content (long customer/title with the
            label toggles maxed). gridTemplateRows untouched — minmax(120px,
            max-content) keeps the vertical grow-with-content behavior. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gridTemplateRows: `repeat(${days.length / 7}, minmax(120px, max-content))` }}>
          {days.map((day, i) => {
            const dateStr = format(day, 'yyyy-MM-dd')
            const dayJobs = jobsByDate[dateStr] || []
            const isCurrentMonth = isSameMonth(day, currentDate)
            const isCurrentDay = isToday(day)
            return (
              <div key={i} onClick={() => onNewJob(dateStr)}
                style={{ border: '1px solid var(--border-subtle)', padding: '6px 8px', cursor: 'pointer', background: isCurrentDay ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : 'transparent', transition: 'background 150ms ease' }}
                onMouseEnter={e => { if (!isCurrentDay) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isCurrentDay) e.currentTarget.style.background = 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: isCurrentDay ? 800 : 500, color: isCurrentDay ? 'var(--accent-ink)' : isCurrentMonth ? 'var(--ink-secondary)' : 'var(--ink-tertiary)', width: 22, height: 22, borderRadius: '50%', background: isCurrentDay ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {format(day, 'd')}
                  </span>
                  {dayJobs.length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '1px 5px', borderRadius: 100 }}>{dayJobs.length}</span>
                  )}
                </div>
                <div>
                  {dayJobs.map((job: any) => (
                    <CalendarJobPill key={job.id} job={job} prefs={prefs} onClick={() => onJobClick(job)} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Week/Day helpers ─────────────────────────────────────────────────────
// Visible workday boundaries (7am-8pm). buildJobClusters uses these for
// in-range bucketing; out-of-range jobs feed the early/late chips. The
// absolute-positioning math that used HOUR_RANGE_MIN + PILL_*_HEIGHT_PCT
// + MAX_VISIBLE_CLUSTER_COLS is gone after the C1 flow rewrite — Week and
// Day now lay out their jobs as a top-to-bottom flow of cluster-blocks,
// each growing to fit its content.
const HOUR_START_MIN = HOURS[0] * 60
const HOUR_END_MIN = (HOURS[HOURS.length - 1] + 1) * 60

function parseTimeToMinutes(t: string | null | undefined): number | null {
  if (!t) return null
  const [h, m] = t.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h * 60 + m
}

function minutesToLabel(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const hh = h % 12 === 0 ? 12 : h % 12
  return m === 0 ? `${hh}:00 ${period}` : `${hh}:${String(m).padStart(2, '0')} ${period}`
}

interface ClusterEntry { job: any; col: number; startMin: number; endMin: number }
interface JobCluster {
  entries: ClusterEntry[]
  totalCols: number
  startMin: number
  endMin: number
}

// Sweep-line cluster builder. Standard Google/Apple Calendar pattern:
// jobs sorted by start; each gets placed into the leftmost column whose
// last job ended at or before the new job's start. A new cluster begins
// when a job's start is >= the running max end of the current cluster.
function buildJobClusters(jobs: any[]): JobCluster[] {
  const parsed = jobs
    .map(j => {
      const startMin = parseTimeToMinutes(j.scheduled_start)
      if (startMin == null) return null
      // Job is in-range if start falls within the visible window.
      if (startMin < HOUR_START_MIN || startMin >= HOUR_END_MIN) return null
      const rawEnd = parseTimeToMinutes(j.scheduled_end)
      // No end time → default short duration; ensures monotonicity.
      let endMin = rawEnd ?? startMin + 30
      if (endMin <= startMin) endMin = startMin + 30
      return { job: j, startMin, endMin }
    })
    .filter((p): p is { job: any; startMin: number; endMin: number } => p != null)
    .sort((a, b) => a.startMin - b.startMin)

  const clusters: JobCluster[] = []
  let current: ClusterEntry[] = []
  let currentMaxEnd = -Infinity

  const flush = () => {
    if (current.length === 0) return
    const totalCols = Math.max(...current.map(e => e.col)) + 1
    clusters.push({
      entries: current,
      totalCols,
      startMin: Math.min(...current.map(e => e.startMin)),
      endMin: Math.max(...current.map(e => e.endMin)),
    })
    current = []
    currentMaxEnd = -Infinity
  }

  for (const p of parsed) {
    if (current.length > 0 && p.startMin >= currentMaxEnd) flush()
    // Find the leftmost column whose latest-end is ≤ this job's start.
    const colEnds: number[] = []
    for (const e of current) {
      colEnds[e.col] = Math.max(colEnds[e.col] ?? -Infinity, e.endMin)
    }
    let assignedCol = colEnds.findIndex(end => end <= p.startMin)
    if (assignedCol === -1) assignedCol = colEnds.length === 0 ? 0 : colEnds.length
    current.push({ job: p.job, col: assignedCol, startMin: p.startMin, endMin: p.endMin })
    if (p.endMin > currentMaxEnd) currentMaxEnd = p.endMin
  }
  flush()

  return clusters
}

// Flow-layout cluster container. Renders N overlapping jobs as a vertical
// stack inside a subtly-styled container that visually signals "these
// overlap." Used by both WeekView and DayView (C1 flow). Single-job
// clusters skip this and render a bare CalendarJobPill at the call site.
function ClusterFlowBlock({
  cluster, prefs, onJobClick,
}: {
  cluster: JobCluster
  prefs: CalendarLabelPrefs
  onJobClick: (job: any) => void
}) {
  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: 'var(--hover-bg)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8,
        padding: '7px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 2px' }}>
        {cluster.entries.length} overlapping · {minutesToLabel(cluster.startMin)} – {minutesToLabel(cluster.endMin)}
      </p>
      {cluster.entries.map(({ job }) => (
        <CalendarJobPill key={job.id} job={job} prefs={prefs} onClick={() => onJobClick(job)} />
      ))}
    </div>
  )
}

function WeekView({ currentDate, jobs, onJobClick, onNewJob, isMobile }: any) {
  const { prefs } = useCalendarLabelPrefs()
  const weekStart = startOfWeek(currentDate)
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const jobsByDay = useMemo(() => {
    const map: Record<string, any[]> = {}
    days.forEach(d => { map[format(d, 'yyyy-MM-dd')] = [] })
    jobs.forEach((job: any) => {
      if (job.scheduled_date && map[job.scheduled_date]) map[job.scheduled_date].push(job)
    })
    return map
  }, [jobs, days])

  // Per-day: clusters of in-range jobs + early/late buckets for the
  // out-of-range chips. Jobs with no scheduled_start are dropped entirely
  // (matches prior behavior — they were rendered at top:0 with default
  // height, effectively unreadable).
  const perDay = useMemo(() => {
    const map: Record<string, { clusters: JobCluster[]; earlyJobs: any[]; lateJobs: any[] }> = {}
    Object.entries(jobsByDay).forEach(([dateStr, dayJobs]) => {
      const early: any[] = []
      const late: any[] = []
      const inRange: any[] = []
      for (const j of dayJobs as any[]) {
        const startMin = parseTimeToMinutes(j.scheduled_start)
        // Null-time jobs join the early bucket. Both columns are nullable in
        // the schema, so this case is real; routing them through the same
        // "↑ N earlier" chip keeps them reachable rather than vanishing
        // entirely (pre-fix they rendered at top:0 with placeholder height).
        if (startMin == null) { early.push(j); continue }
        if (startMin < HOUR_START_MIN) early.push(j)
        else if (startMin >= HOUR_END_MIN) late.push(j)
        else inRange.push(j)
      }
      map[dateStr] = { clusters: buildJobClusters(inRange), earlyJobs: early, lateJobs: late }
    })
    return map
  }, [jobsByDay])

  // ─── Popover state (cluster overflow + out-of-range chips) ──────────────
  // Mirrors MonthView's popover pattern (commit 3de6ec6): position:fixed
  // anchored to a getBoundingClientRect, dismiss on click-outside / Escape /
  // scroll / resize. Capture-phase scroll listener catches scrolls in the
  // calendar's overflow:auto container.
  const [openPopover, setOpenPopover] = useState<{
    title: string
    subtitle: string
    jobs: any[]
    anchorRect: DOMRect
  } | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openPopover) return
    const onMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpenPopover(null)
      }
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenPopover(null) }
    const dismiss = () => setOpenPopover(null)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', dismiss, true)
    window.addEventListener('resize', dismiss)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', dismiss, true)
      window.removeEventListener('resize', dismiss)
    }
  }, [openPopover])

  const popoverPos = useMemo(() => {
    if (!openPopover) return null
    const W = 280
    const H_EST = Math.min(60 + openPopover.jobs.length * 36, 400)
    const rect = openPopover.anchorRect
    const vh = window.innerHeight
    const vw = window.innerWidth
    const top = rect.bottom + 4 + H_EST > vh
      ? Math.max(8, rect.top - H_EST - 4)
      : rect.bottom + 4
    const left = rect.left + W > vw - 8
      ? Math.max(8, rect.right - W)
      : rect.left
    return { top, left, width: W, maxHeight: 400 }
  }, [openPopover])

  // In flow blocks, time is the ordering signal — force it visible regardless
  // of the user's toggle preference (their preference still applies to
  // MonthView, where rows + columns convey order without per-pill time).
  const flowPrefs = useMemo(() => ({ ...prefs, time: true }), [prefs])

  return (
    <div style={isMobile ? { display: 'flex', flexDirection: 'column' } : { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sticky day-of-week header (Sun/Mon/.../Sat + date). 7 columns to
          match the flow body below — no left hour-rail anymore in C1 flow.
          minmax(0, 1fr) mirrors the body grid so the columns can't desync. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        {days.map((day, i) => (
          <div key={i} style={{ padding: '10px 8px', textAlign: 'center', borderLeft: i > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>{format(day, 'EEE')}</p>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: isToday(day) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: isToday(day) ? 'var(--accent-ink)' : 'var(--ink-primary)' }}>{format(day, 'd')}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Flow body: 7 independent day-columns side-by-side, each flowing its
          own clusters top-to-bottom (Option α from the spec). Tall columns
          extend the body height; short columns have empty space below. The
          whole body scrolls vertically. No cross-column hour-alignment. On
          mobile this wrapper becomes a natural-height block — the page
          itself scrolls via AppLayout's <main>. */}
      <div style={isMobile ? {} : { flex: 1, overflow: 'auto' }}>
        {/* Body: 7-column GRID (not flex) so it uses the same mechanism as
            the header and CANNOT desync by construction. minmax(0, 1fr)
            lets columns shrink below pill min-content; the pill's existing
            overflow:hidden + text-overflow:ellipsis truncates instead of
            pushing the cell wider. Per-column flex-direction:column +
            minHeight:200 + gap preserved — vertical stacking unchanged. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', alignItems: 'flex-start', minHeight: '100%' }}>
          {days.map((day, i) => {
            const dateStr = format(day, 'yyyy-MM-dd')
            const { clusters, earlyJobs, lateJobs } = perDay[dateStr] || { clusters: [], earlyJobs: [], lateJobs: [] }
            const isEmpty = clusters.length === 0 && earlyJobs.length === 0 && lateJobs.length === 0
            return (
              <div key={i} onClick={() => onNewJob(dateStr)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: '10px 6px',
                  cursor: 'pointer',
                  borderLeft: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                  background: isToday(day) ? 'color-mix(in srgb, var(--accent) 2%, transparent)' : 'transparent',
                  minHeight: 200,
                }}>

                {earlyJobs.length > 0 && (
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      setOpenPopover({
                        title: `Before ${minutesToLabel(HOUR_START_MIN)}`,
                        subtitle: `${earlyJobs.length} ${earlyJobs.length === 1 ? 'job' : 'jobs'} outside visible hours`,
                        jobs: earlyJobs,
                        anchorRect: e.currentTarget.getBoundingClientRect(),
                      })
                    }}
                    title={`${earlyJobs.length} ${earlyJobs.length === 1 ? 'job' : 'jobs'} earlier than ${minutesToLabel(HOUR_START_MIN)}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '4px 8px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 100, fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', cursor: 'pointer' }}
                  >
                    <ChevronUp style={{ width: 11, height: 11 }} />
                    {earlyJobs.length} earlier
                  </button>
                )}

                {clusters.map((cluster, ci) => (
                  cluster.entries.length === 1
                    ? <CalendarJobPill key={ci} job={cluster.entries[0].job} prefs={flowPrefs} onClick={() => onJobClick(cluster.entries[0].job)} />
                    : <ClusterFlowBlock key={ci} cluster={cluster} prefs={flowPrefs} onJobClick={onJobClick} />
                ))}

                {lateJobs.length > 0 && (
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      setOpenPopover({
                        title: `After ${minutesToLabel(HOUR_END_MIN)}`,
                        subtitle: `${lateJobs.length} ${lateJobs.length === 1 ? 'job' : 'jobs'} outside visible hours`,
                        jobs: lateJobs,
                        anchorRect: e.currentTarget.getBoundingClientRect(),
                      })
                    }}
                    title={`${lateJobs.length} ${lateJobs.length === 1 ? 'job' : 'jobs'} later than ${minutesToLabel(HOUR_END_MIN)}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '4px 8px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 100, fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', cursor: 'pointer' }}
                  >
                    <ChevronDown style={{ width: 11, height: 11 }} />
                    {lateJobs.length} later
                  </button>
                )}

                {isEmpty && (
                  <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', textAlign: 'center', margin: '24px 0 0', opacity: 0.5 }}>—</p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {openPopover && popoverPos && (
        <div
          ref={popoverRef}
          style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, width: popoverPos.width, maxHeight: popoverPos.maxHeight, zIndex: 50, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--popup-shadow)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {openPopover.title}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                {openPopover.subtitle}
              </p>
            </div>
            <button
              onClick={() => setOpenPopover(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              aria-label="Close"
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
          <div style={{ padding: 8, overflowY: 'auto' }}>
            {openPopover.jobs.map((job: any) => (
              <CalendarJobPill
                key={job.id}
                job={job}
                onClick={() => { onJobClick(job); setOpenPopover(null) }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DayView({ currentDate, jobs, onJobClick, onNewJob, isMobile }: any) {
  const { prefs } = useCalendarLabelPrefs()
  const dateStr = format(currentDate, 'yyyy-MM-dd')
  const dayJobs = jobs.filter((j: any) => j.scheduled_date === dateStr).sort((a: any, b: any) => (a.scheduled_start || '').localeCompare(b.scheduled_start || ''))

  // Cluster-aware layout. Mirrors WeekView's perDay split exactly, just for
  // one day. Null-start and out-of-window jobs feed the earlier/later chips
  // so they stay reachable instead of stacking at the top of the timeline.
  const { clusters, earlyJobs, lateJobs } = useMemo(() => {
    const early: any[] = []
    const late: any[] = []
    const inRange: any[] = []
    for (const j of dayJobs as any[]) {
      const startMin = parseTimeToMinutes(j.scheduled_start)
      if (startMin == null) { early.push(j); continue }
      if (startMin < HOUR_START_MIN) early.push(j)
      else if (startMin >= HOUR_END_MIN) late.push(j)
      else inRange.push(j)
    }
    return { clusters: buildJobClusters(inRange), earlyJobs: early, lateJobs: late }
  }, [dayJobs])

  // Overflow / out-of-range popover. Identical pattern to WeekView's; kept
  // local because state and dismissal handlers are stateful and don't
  // share well across views.
  const [openPopover, setOpenPopover] = useState<{
    title: string
    subtitle: string
    jobs: any[]
    anchorRect: DOMRect
  } | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openPopover) return
    const onMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpenPopover(null)
      }
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenPopover(null) }
    const dismiss = () => setOpenPopover(null)
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', dismiss, true)
    window.addEventListener('resize', dismiss)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', dismiss, true)
      window.removeEventListener('resize', dismiss)
    }
  }, [openPopover])

  const popoverPos = useMemo(() => {
    if (!openPopover) return null
    const W = 320
    const H_EST = Math.min(60 + openPopover.jobs.length * 36, 400)
    const rect = openPopover.anchorRect
    const vh = window.innerHeight
    const vw = window.innerWidth
    const top = rect.bottom + 4 + H_EST > vh
      ? Math.max(8, rect.top - H_EST - 4)
      : rect.bottom + 4
    const left = rect.left + W > vw - 8
      ? Math.max(8, rect.right - W)
      : rect.left
    return { top, left, width: W, maxHeight: 400 }
  }, [openPopover])

  // Time-as-ordering-signal: force prefs.time on for flow blocks (see WeekView).
  const flowPrefs = useMemo(() => ({ ...prefs, time: true }), [prefs])

  return (
    <div style={isMobile ? { display: 'flex' } : { flex: 1, display: 'flex', overflow: 'hidden' }}>
      {/* Main day flow — single column, blocks flow top-to-bottom, each block
          grows to fit its content. No hour rail, no absolute positioning,
          no +N more (overlapping clusters render as a stacked container).
          On mobile the bounded-flex chain is dissolved so the page scrolls
          via AppLayout's <main>. */}
      <div
        onClick={() => onNewJob(dateStr)}
        style={isMobile ? { cursor: 'pointer' } : { flex: 1, overflow: 'auto', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', minHeight: '100%' }}>
          {earlyJobs.length > 0 && (
            <button
              onClick={e => {
                e.stopPropagation()
                setOpenPopover({
                  title: `Before ${minutesToLabel(HOUR_START_MIN)}`,
                  subtitle: `${earlyJobs.length} ${earlyJobs.length === 1 ? 'job' : 'jobs'} outside visible hours`,
                  jobs: earlyJobs,
                  anchorRect: e.currentTarget.getBoundingClientRect(),
                })
              }}
              title={`${earlyJobs.length} ${earlyJobs.length === 1 ? 'job' : 'jobs'} earlier than ${minutesToLabel(HOUR_START_MIN)}`}
              style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 100, fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', cursor: 'pointer' }}
            >
              <ChevronUp style={{ width: 12, height: 12 }} />
              {earlyJobs.length} earlier
            </button>
          )}

          {clusters.map((cluster, ci) => (
            cluster.entries.length === 1
              ? <CalendarJobPill key={ci} job={cluster.entries[0].job} prefs={flowPrefs} onClick={() => onJobClick(cluster.entries[0].job)} />
              : <ClusterFlowBlock key={ci} cluster={cluster} prefs={flowPrefs} onJobClick={onJobClick} />
          ))}

          {lateJobs.length > 0 && (
            <button
              onClick={e => {
                e.stopPropagation()
                setOpenPopover({
                  title: `After ${minutesToLabel(HOUR_END_MIN)}`,
                  subtitle: `${lateJobs.length} ${lateJobs.length === 1 ? 'job' : 'jobs'} outside visible hours`,
                  jobs: lateJobs,
                  anchorRect: e.currentTarget.getBoundingClientRect(),
                })
              }}
              title={`${lateJobs.length} ${lateJobs.length === 1 ? 'job' : 'jobs'} later than ${minutesToLabel(HOUR_END_MIN)}`}
              style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 100, fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', cursor: 'pointer' }}
            >
              <ChevronDown style={{ width: 12, height: 12 }} />
              {lateJobs.length} later
            </button>
          )}

          {dayJobs.length === 0 && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: '60px 16px' }}>
              <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No jobs scheduled</p>
              <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Click anywhere to add a job</p>
            </div>
          )}
        </div>
      </div>

      {openPopover && popoverPos && (
        <div
          ref={popoverRef}
          style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, width: popoverPos.width, maxHeight: popoverPos.maxHeight, zIndex: 50, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--popup-shadow)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {openPopover.title}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                {openPopover.subtitle}
              </p>
            </div>
            <button
              onClick={() => setOpenPopover(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              aria-label="Close"
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
          <div style={{ padding: 8, overflowY: 'auto' }}>
            {openPopover.jobs.map((job: any) => (
              <CalendarJobPill
                key={job.id}
                job={job}
                onClick={() => { onJobClick(job); setOpenPopover(null) }}
              />
            ))}
          </div>
        </div>
      )}
      <div style={{ width: 220, borderLeft: '1px solid var(--border-subtle)', padding: '16px 14px', overflow: 'auto', flexShrink: 0 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>{format(currentDate, 'EEEE, MMM d')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Per-status count summary. The 4 active statuses each get their
              own pill; the 4 hold statuses collapse into a single combined
              "On hold" pill (with the customer-hold token as its color hook)
              so the rail doesn't double in length when holds are sparse. */}
          {['scheduled', 'en_route', 'on_site', 'complete'].map(status => {
            const count = dayJobs.filter((j: any) => j.status === status).length
            const cfg = JOB_STATUS_CONFIG[status]
            if (count === 0) return null
            return (
              <div key={status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: cfg.bg, borderRadius: 8, border: `1px solid ${cfg.border}` }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: cfg.color }}>{count}</span>
              </div>
            )
          })}
          {(() => {
            const heldCount = dayJobs.filter((j: any) => HOLD_STATUSES.includes(j.status)).length
            if (heldCount === 0) return null
            const cfg = JOB_STATUS_CONFIG.on_customer_hold
            return (
              <div key="on_hold" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: cfg.bg, borderRadius: 8, border: `1px solid ${cfg.border}` }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>On hold</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: cfg.color }}>{heldCount}</span>
              </div>
            )
          })()}
        </div>
        {dayJobs.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>All jobs</p>
            {dayJobs.map((job: any) => {
              const cfg = JOB_STATUS_CONFIG[job.status]
              return (
                <div key={job.id} onClick={() => onJobClick(job)}
                  style={{ padding: '7px 9px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, marginBottom: 5, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{job.title}</p>
                  <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{job.scheduled_start} · <span style={{ color: cfg?.color }}>{cfg?.label}</span></p>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Map View ─────────────────────────────────────────────────────────────────
function MapView({ jobs, onJobClick }: any) {
  const [selectedJob, setSelectedJob] = useState<any>(null)
  const jobsWithAddress = jobs.filter((j: any) => j.address_line1 || j.address_city)

  // Build a Google Maps search URL with all job addresses. Single + multi-job
  // paths currently both center on the first job; multi-stop routing is on the
  // roadmap. mapsUrl includes zip + country for higher geocoding precision.
  const mapUrl = useMemo(() => {
    if (jobsWithAddress.length === 0) return null
    return mapsUrl(fromAddressPrefix(jobsWithAddress[0]))
  }, [jobsWithAddress])

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', padding: '0 28px 28px', gap: 16 }}>

      {/* Left: job list */}
      <div style={{ width: 320, flexShrink: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            {jobs.length} jobs this period
          </p>
          {mapUrl && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}>
              <ExternalLink style={{ width: 12, height: 12 }} /> Open in Maps
            </a>
          )}
        </div>

        {jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px' }}>
            <Truck style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No jobs in this period</p>
          </div>
        ) : (
          jobs.map((job: any) => {
            const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
            const isSelected = selectedJob?.id === job.id
            const hasAddress = job.address_line1 || job.address_city
            const jobMapsUrl = mapsUrl(fromAddressPrefix(job))

            return (
              <div key={job.id}
                onClick={() => { setSelectedJob(isSelected ? null : job) }}
                style={{ background: isSelected ? 'var(--accent-dim)' : 'var(--surface)', border: `1px solid ${isSelected ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderLeft: `3px solid ${cfg.color}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 150ms ease' }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'var(--surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                    {job._hasConflict && <ConflictIndicator size={11} />}
                    {job.series_id && <RecurringIndicator size={11} />}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number}</span>
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</p>
                <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>{job.contact_name}</p>
                {hasAddress ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin style={{ width: 11, height: 11, color: 'var(--accent)', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                        {formatAddress(fromAddressPrefix(job))}
                      </span>
                    </div>
                    {jobMapsUrl && (
                      <a href={jobMapsUrl} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>
                        <Navigation style={{ width: 10, height: 10 }} /> Go
                      </a>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>No address</span>
                )}
                {job.scheduled_date && (
                  <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '5px 0 0' }}>
                    {job.scheduled_date}{job.scheduled_start ? ` · ${job.scheduled_start}` : ''}
                    {job.assigned_agent_name ? ` · ${job.assigned_agent_name}` : ''}
                  </p>
                )}
                {isSelected && (
                  <button
                    onClick={e => { e.stopPropagation(); onJobClick(job) }}
                    className="btn-primary"
                    style={{ width: '100%', marginTop: 10, justifyContent: 'center', fontSize: 12, padding: '7px' }}>
                    Open job details
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Right: map */}
      <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden', position: 'relative', minHeight: 400 }}>
        {jobsWithAddress.length === 0 ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <MapPin style={{ width: 40, height: 40, color: 'var(--ink-tertiary)' }} />
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-tertiary)', margin: 0 }}>No jobs with addresses</p>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', maxWidth: 260 }}>
              Add addresses to your jobs to see them plotted on the map
            </p>
          </div>
        ) : (
          <>
            {/* Map placeholder — shows address list with open in maps links */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{jobsWithAddress.length} job locations</p>
                  <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Click any address to open in Google Maps</p>
                </div>
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(jobsWithAddress.map((j: any) => formatAddress(fromAddressPrefix(j))).join(' | '))}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--accent)', borderRadius: 9, color: 'var(--accent-ink)', textDecoration: 'none', fontSize: 12, fontWeight: 700 }}>
                  <ExternalLink style={{ width: 13, height: 13 }} /> Open all in Maps
                </a>
              </div>

              {/* Job address grid */}
              <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                  {jobsWithAddress.map((job: any) => {
                    const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
                    const parts = fromAddressPrefix(job)
                    const addr = formatAddress(parts)
                    const jobMapsUrl = mapsUrl(parts) ?? '#'
                    return (
                      <a key={job.id} href={jobMapsUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'block', padding: '12px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 10, textDecoration: 'none', transition: 'background 150ms ease' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--hover-bg)'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 100, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                          {job.assigned_agent_name && (
                            <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{job.assigned_agent_name}</span>
                          )}
                        </div>
                        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</p>
                        <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '0 0 5px' }}>{job.contact_name}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin style={{ width: 10, height: 10, color: 'var(--accent)', flexShrink: 0 }} />
                          <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{addr}</span>
                          <ExternalLink style={{ width: 10, height: 10, color: 'var(--accent)', flexShrink: 0, marginLeft: 'auto' }} />
                        </div>
                      </a>
                    )
                  })}
                </div>

                {/* Honest tip — embedded map is on the roadmap. */}
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid var(--accent-dim)', borderRadius: 10 }}>
                  <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.6 }}>
                    💡 An embedded map view is on the roadmap. For now, click any address above to open it in your device's map app.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FieldHub() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const jobIdParam = searchParams.get('job') || ''

  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'calendar'
  )

  useEffect(() => {
    const tab = searchParams.get('tab') as Tab | null
    const valid: Tab[] = ['calendar', 'board', 'map', 'crew']
    if (tab && valid.includes(tab) && tab !== activeTab) setActiveTab(tab)
    else if (!tab && activeTab !== 'calendar') setActiveTab('calendar')
  }, [searchParams])

  const switchTab = (tab: Tab) => {
    setActiveTab(tab)
    setSearchParams(tab !== 'calendar' ? { tab } : {})
  }

  const isMobile = useIsMobile()
  const [calView, setCalView] = useState<'month' | 'week' | 'day'>('week')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [statusFilter, setStatusFilter] = useState('all')
  const [showNewModal, setShowNewModal] = useState(false)
  const [newJobDate, setNewJobDate] = useState('')
  const [selectedJob, setSelectedJob] = useState<any>(null)
  const [editingJob, setEditingJob] = useState<any>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 200)
    return () => clearTimeout(t)
  }, [searchInput])

  // Calendar/map filter state. Persisted to localStorage so the operator's
  // last view comes back on reload. Empty array on calStatusFilter means
  // "no status filter" (show all); the dispatch UI uses this as the "All"
  // affordance. The Board view has its own statusFilter and boardJobs path —
  // these two filters drive the calendar + map only, never the Board.
  const STATUS_FILTER_KEY  = 'orkestra-cal-status-filter'
  const AGENT_FILTER_KEY   = 'orkestra-cal-agent-filter'
  const INVOICE_FILTER_KEY = 'orkestra-cal-invoice-filter'
  type InvoiceFilter = 'all' | 'ready' | 'invoiced'
  const [calStatusFilter, setCalStatusFilter] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STATUS_FILTER_KEY)
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter((s: any) => typeof s === 'string') : []
    } catch { return [] }
  })
  const [calAgentFilter, setCalAgentFilter] = useState<string>(() => {
    try { return localStorage.getItem(AGENT_FILTER_KEY) || 'all' }
    catch { return 'all' }
  })
  const [calInvoiceFilter, setCalInvoiceFilter] = useState<InvoiceFilter>(() => {
    try {
      const raw = localStorage.getItem(INVOICE_FILTER_KEY)
      // Defensive restore — only honor the 3 known values, else fall back to 'all'.
      return (raw === 'ready' || raw === 'invoiced') ? raw : 'all'
    } catch { return 'all' }
  })
  useEffect(() => {
    try { localStorage.setItem(STATUS_FILTER_KEY, JSON.stringify(calStatusFilter)) } catch { /* ignore */ }
  }, [calStatusFilter])
  useEffect(() => {
    try { localStorage.setItem(AGENT_FILTER_KEY, calAgentFilter) } catch { /* ignore */ }
  }, [calAgentFilter])
  useEffect(() => {
    try { localStorage.setItem(INVOICE_FILTER_KEY, calInvoiceFilter) } catch { /* ignore */ }
  }, [calInvoiceFilter])

  const dateRange = useMemo(() => {
    if (calView === 'month') return {
      start: format(startOfMonth(addDays(startOfMonth(currentDate), -7)), 'yyyy-MM-dd'),
      end: format(endOfMonth(addDays(endOfMonth(currentDate), 7)), 'yyyy-MM-dd'),
    }
    if (calView === 'week') return {
      start: format(startOfWeek(currentDate), 'yyyy-MM-dd'),
      end: format(endOfWeek(currentDate), 'yyyy-MM-dd'),
    }
    return {
      start: format(currentDate, 'yyyy-MM-dd'),
      end: format(currentDate, 'yyyy-MM-dd'),
    }
  }, [calView, currentDate])

  const { data: allJobs = [] } = useQuery({
    queryKey: ['field-jobs-cal', workspace?.id, dateRange.start, dateRange.end],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_jobs').select('*, location:locations(id, name, company_id, company:companies(id, name)), vendor:vendors(id, name), service_line:service_lines(id, name)')
        .eq('workspace_id', workspace.id)
        .gte('scheduled_date', dateRange.start)
        .lte('scheduled_date', dateRange.end)
        .order('scheduled_start')
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    refetchInterval: 30000,
  })

  // Unscheduled jobs: workspace-scoped, scheduled_date IS NULL, not in a
  // terminal status. Lives outside the date-range query because NULL dates
  // fail both .gte and .lte comparisons in Postgres, so the calendar query
  // can never surface them. Driven from the calendar-tab "Needs scheduling"
  // tray below the filter row. Always enabled so the tray badge stays
  // accurate without view-switching latency.
  const { data: unscheduledJobs = [] } = useQuery({
    queryKey: ['field-jobs-unscheduled', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_jobs')
        .select('*, location:locations(id, name, company_id, company:companies(id, name)), vendor:vendors(id, name), service_line:service_lines(id, name)')
        .eq('workspace_id', workspace.id)
        .is('scheduled_date', null)
        .not('status', 'in', '(complete,cancelled)')
        .order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const [unscheduledTrayOpen, setUnscheduledTrayOpen] = useState(false)

  // Separate query for the AR "Ready for invoice" filter: pulls all
  // completed-no-invoice jobs across the workspace, NOT bounded by the
  // calendar date range, so AR can scan older stale unbilled work. Same join
  // shape as the calendar query so downstream JobCard rendering is identical.
  // Enabled only when the filter is active; idle otherwise.
  const { data: readyForInvoiceJobs = [] } = useQuery({
    queryKey: ['field-jobs-ready-for-invoice', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_jobs').select('*, location:locations(id, name, company_id, company:companies(id, name)), vendor:vendors(id, name), service_line:service_lines(id, name)')
        .eq('workspace_id', workspace.id)
        .eq('status', 'complete')
        .is('invoice_id', null)
        .order('scheduled_date', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id && statusFilter === 'ready_for_invoice',
  })

  // Lazy recurring-series window-keeper. Runs alongside the calendar query
  // (NOT blocking render) whenever the visible end advances. The keeper
  // extends every active series forward through (visibleEnd + 30d buffer)
  // via the shared buildOccurrenceJobs generator — single source of truth
  // for recurrence math. staleTime de-dupes rapid prev/next nav within a
  // minute; the queryKey on dateRange.end means moving to a later window
  // is a fresh run.
  const { data: keepResult, error: keepError } = useQuery({
    queryKey: ['keep-recurring', workspace?.id, dateRange.end],
    queryFn: () => keepRecurringWindow(supabase, workspace!.id, dateRange.end),
    enabled: !!workspace?.id,
    staleTime: 60_000,
  })

  // Defensive visibility for keeper failures — a silent keeper is a real
  // bug class (Chunk 5A surfaced the swallow-via-useQuery pattern). Toast
  // gives the operator a visible signal; console.error gives a starting
  // point for debugging. The keeper itself logs per-stage details before
  // throwing, so the outer log here stays minimal.
  useEffect(() => {
    if (!keepError) return
    console.error('[keep-recurring] keeper failed:', keepError)
    toast.error(`Recurring keeper failed — see console`)
  }, [keepError, workspace?.id, dateRange.end])

  // When the keeper materialized new occurrences, invalidate the calendar
  // query so they appear in the current view without a manual refresh.
  // Ref-guarded so the effect only fires once per successful keeper run that
  // actually produced rows.
  const lastKeptRef = useRef<typeof keepResult | null>(null)
  useEffect(() => {
    if (!keepResult || keepResult === lastKeptRef.current) return
    lastKeptRef.current = keepResult
    if (keepResult.occurrences > 0) {
      queryClient.invalidateQueries({ queryKey: ['field-jobs-cal', workspace?.id] })
    }
  }, [keepResult, queryClient, workspace?.id])

  // Deep-link fallback. When ?job=:id targets a job whose scheduled_date is
  // outside the current calendar window, allJobs won't contain it. Fetch the
  // single row by id with the same join shape so JobDrawer renders identically.
  const { data: jobById = null } = useQuery({
    queryKey: ['field-job-by-id', jobIdParam, workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from('field_jobs')
        .select('*, location:locations(id, name, company_id, company:companies(id, name)), vendor:vendors(id, name), service_line:service_lines(id, name)')
        .eq('id', jobIdParam)
        .maybeSingle()
      if (error) throw error
      return data
    },
    enabled: !!workspace?.id && !!jobIdParam && !allJobs.some((j: any) => j.id === jobIdParam),
  })

  // ─── Vendor lifecycle latest-state — single batched query ─────────
  // Keyed by the sorted union of visible job ids. PostgREST does the
  // ordering + limit on the embedded vendor_assignment_events rows
  // server-side (one round-trip), so we end up with at most one event
  // per assignment. The reducer below collapses accepted-assignment
  // rows to a Map<field_job_id, {event_type, created_at, note}> the
  // CalendarJobPill consumes. Avoids the N+1 per-card fetch that a
  // per-JobCard useQuery would create on a busy board.
  const vendorStateJobIds = useMemo(() => {
    const ids = new Set<string>()
    for (const j of allJobs) ids.add(j.id)
    for (const j of unscheduledJobs) ids.add(j.id)
    return Array.from(ids).sort()
  }, [allJobs, unscheduledJobs])

  const { data: vendorStateRows = [] } = useQuery({
    queryKey: ['vendor-states-by-job', workspace?.id, vendorStateJobIds.join(',')],
    enabled: !!workspace?.id && vendorStateJobIds.length > 0,
    refetchInterval: 30000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_assignments')
        .select('field_job_id, vendor_assignment_events(event_type, created_at, note)')
        .in('field_job_id', vendorStateJobIds)
        .eq('status', 'accepted')
        .order('created_at', { foreignTable: 'vendor_assignment_events', ascending: false })
        .limit(1, { foreignTable: 'vendor_assignment_events' })
      if (error) throw error
      return (data ?? []) as Array<{ field_job_id: string; vendor_assignment_events: Array<{ event_type: VendorLifecycleEvent; created_at: string; note: string | null }> }>
    },
  })

  const vendorStateByJobId = useMemo(() => {
    const m = new Map<string, { event_type: VendorLifecycleEvent; created_at: string; note: string | null }>()
    for (const r of vendorStateRows) {
      const ev = r.vendor_assignment_events?.[0]
      if (ev) m.set(r.field_job_id, ev)
    }
    return m
  }, [vendorStateRows])

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-field-hub', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email, phone, company_name').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['companies-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('companies').select('id, name').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: locationsList = [] } = useQuery({
    queryKey: ['locations-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('locations').select('id, company_id, name, store_number, address_line1, city, state').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: agents = [], isSuccess: agentsLoaded } = useQuery({
    queryKey: ['agents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Agent-filter staleness guard. If a stored profile id no longer matches a
  // real teammate (employee left, workspace switched, profile deleted), the
  // filter would silently hide every job. Gated on isSuccess so we don't
  // invalidate during the first-paint window when agents is still the [] default.
  useEffect(() => {
    if (!agentsLoaded) return
    if (calAgentFilter === 'all' || calAgentFilter === 'unassigned') return
    if (!agents.find((a: any) => a.id === calAgentFilter)) setCalAgentFilter('all')
  }, [agentsLoaded, agents, calAgentFilter])

  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendors').select('id, name, status').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: serviceLinesList = [] } = useQuery({
    queryKey: ['service-lines-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('service_lines').select('id, name, is_active').eq('workspace_id', workspace.id).eq('is_active', true).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase.from('field_jobs').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      // Setting a date on an unscheduled job moves it out of the tray;
      // clearing a date moves a scheduled job into the tray. Invalidate
      // both queries together so neither view goes stale on an edit.
      queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
      queryClient.invalidateQueries({ queryKey: ['field-jobs-unscheduled'] })
    },
    onError: () => toast.error('Failed to update'),
  })

  const handleUpdate = useCallback((id: string, updates: any) => {
    // Schedule-gain notify (NULL → date). Pull prior state from
    // react-query's cache directly so we capture the pre-mutation value
    // before the cache flushes. One-shot semantics: reschedules never
    // re-fire because the cached prior is already non-null.
    const cacheCal = queryClient.getQueriesData<any[]>({ queryKey: ['field-jobs-cal'] })
    const cacheUnsch = queryClient.getQueriesData<any[]>({ queryKey: ['field-jobs-unscheduled'] })
    const findIn = (entries: Array<[unknown, unknown]>): any => {
      for (const [, data] of entries) {
        if (!Array.isArray(data)) continue
        const hit = data.find((j: any) => j?.id === id)
        if (hit) return hit
      }
      return null
    }
    const priorJob = findIn(cacheCal) ?? findIn(cacheUnsch) ?? selectedJob ?? null
    const gainedSchedule =
      priorJob && !priorJob.scheduled_date
      && typeof updates?.scheduled_date === 'string' && updates.scheduled_date.length > 0
    const becameComplete = updates?.status === 'complete'

    // Per-call onSuccess gates the notify on confirmed mutation success.
    // Firing synchronously after mutate() would email the client about
    // a change that hadn't persisted (and might fail). The mutation's
    // own onError surfaces 'Failed to update'.
    updateMutation.mutate({ id, updates }, {
      onSuccess: () => {
        if (gainedSchedule) notifyPortal({ event: 'job_scheduled', job_id: id })
        if (becameComplete) notifyPortal({ event: 'job_completed', job_id: id })
      },
    })
    if (selectedJob?.id === id) setSelectedJob((prev: any) => ({ ...prev, ...updates }))
  }, [selectedJob, queryClient, updateMutation])

  const navDate = (dir: 1 | -1) => {
    // Month-nav snaps currentDate to the 1st of the target month.
    // date-fns addMonths(May 31, 1) = June 30 (clamps to last valid day) —
    // wrapping in startOfMonth eliminates that drift so currentDate
    // always represents the 1st of the viewed month after month-nav.
    if (calView === 'month') setCurrentDate(startOfMonth(addMonths(currentDate, dir === 1 ? 1 : -1)))
    else if (calView === 'week') setCurrentDate(dir === 1 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1))
    else setCurrentDate(dir === 1 ? addDays(currentDate, 1) : subDays(currentDate, 1))
  }

  // View-switch re-anchor: switching Month→Week→Day shouldn't inherit a
  // drifted currentDate (e.g. month-nav overflow leaving us at the 30th).
  // Anchor to today if today is within the currently-viewed range, else
  // to the start of that range. Keeps context near where the operator was.
  const switchView = (newView: 'month' | 'week' | 'day') => {
    if (newView === calView) return
    let rangeStart: Date
    let rangeEnd: Date
    if (calView === 'month') { rangeStart = startOfMonth(currentDate); rangeEnd = endOfMonth(currentDate) }
    else if (calView === 'week') { rangeStart = startOfWeek(currentDate); rangeEnd = endOfWeek(currentDate) }
    else { rangeStart = currentDate; rangeEnd = currentDate }
    const today = new Date()
    const anchor = (today >= rangeStart && today <= rangeEnd) ? today : rangeStart
    setCurrentDate(anchor)
    setCalView(newView)
  }

  const handleNewJob = (date?: string) => {
    setNewJobDate(date || format(currentDate, 'yyyy-MM-dd'))
    setShowNewModal(true)
  }

  // Deep-link sync. When ?job=:id is set and no drawer is open, find the
  // job (in-window first, by-id fallback second) and open the drawer.
  // Gated on selectedJob === null so closing the drawer doesn't re-trigger.
  useEffect(() => {
    if (!jobIdParam || selectedJob) return
    const found = allJobs.find((j: any) => j.id === jobIdParam) || jobById
    if (found) setSelectedJob(found)
  }, [jobIdParam, selectedJob, allJobs, jobById])

  // Close drawer + clear ?job= from URL together. Without the URL clear, the
  // deep-link sync effect above re-opens the drawer immediately on the next
  // render (selectedJob becomes null but jobIdParam is still set).
  const closeDrawer = useCallback(() => {
    setSelectedJob(null)
    if (searchParams.get('job')) {
      const next = new URLSearchParams(searchParams)
      next.delete('job')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // KPI counters are deliberately period totals, NOT filter-responsive:
  // the operator reading "X active now / Y scheduled" expects a workspace
  // snapshot of dispatch state, not a count of whatever happens to be
  // visible under the current status/agent filter. If you want filtered
  // counts, render them inside the filter row (see hiddenCount below).
  const metrics = useMemo(() => ({
    total: allJobs.length,
    active: allJobs.filter((j: any) => ['en_route', 'on_site'].includes(j.status)).length,
    scheduled: allJobs.filter((j: any) => j.status === 'scheduled').length,
    complete: allJobs.filter((j: any) => j.status === 'complete').length,
    held: allJobs.filter((j: any) => HOLD_STATUSES.includes(j.status)).length,
  }), [allJobs])

  // Conflict map computed against the full unfiltered set so a filtered view
  // (search / status / agent) still flags conflicts with hidden jobs. An
  // operator filtered to a single tech should still see that the tech's
  // 10am collides with another tech's 10am, even though that other job
  // isn't in view.
  const conflictMap = useMemo(() => detectConflictsInList(allJobs), [allJobs])

  // Synthetic `_hasConflict` flag is injected onto every job downstream views
  // render. Cards across MonthView / WeekView / DayView / List / Crew / Map
  // read this single boolean rather than re-querying or re-deriving.
  //
  // filteredJobs is search-only on purpose — the Board view's boardJobs chain
  // composes its own statusFilter pill on top of this, and we don't want the
  // calendar/map filter row (calStatusFilter + calAgentFilter) to silently
  // leak into Board behavior. Calendar/Map read calendarJobs (next memo)
  // which layers the new filters on top of filteredJobs.
  const filteredJobs = useMemo(() => {
    const annotate = (j: any) => ({
      ...j,
      _hasConflict: conflictMap.has(j.id),
      _vendorLatest: vendorStateByJobId.get(j.id) ?? null,
    })
    if (!search) return allJobs.map(annotate)
    const s = search.toLowerCase()
    return allJobs.filter((j: any) =>
      j.job_number?.toLowerCase().includes(s) ||
      j.contact_name?.toLowerCase().includes(s) ||
      j.company_name?.toLowerCase().includes(s) ||
      j.address_line1?.toLowerCase().includes(s) ||
      j.address_city?.toLowerCase().includes(s) ||
      j.address_state?.toLowerCase().includes(s) ||
      j.location?.name?.toLowerCase().includes(s) ||
      j.location?.company?.name?.toLowerCase().includes(s) ||
      j.work_order_number?.toLowerCase().includes(s) ||
      j.purchase_order_number?.toLowerCase().includes(s)
    ).map(annotate)
  }, [allJobs, search, conflictMap, vendorStateByJobId])

  // Calendar + Map filter composition: AND across status (multi-select OR
  // within itself, empty = no filter), agent (single-select, 'all' = no
  // filter, 'unassigned' = null assigned_agent_id, otherwise id match — we
  // filter on assigned_agent_id, never the denormalized name), and invoice
  // (derived from status='complete' + invoice_id presence). All layered on
  // top of filteredJobs so the existing search filter still applies.
  //
  // The invoice filter is intentionally AND-composed with status: picking
  // "Ready to invoice" while the status pills exclude 'complete' will
  // legitimately produce an empty set. The "n hidden" indicator below the
  // filter row is the feedback for that case — we do NOT auto-modify
  // calStatusFilter when an invoice pill activates, because that would
  // surprise an operator who had carefully composed both filters.
  const calendarJobs = useMemo(() => {
    if (
      calStatusFilter.length === 0 &&
      calAgentFilter === 'all' &&
      calInvoiceFilter === 'all'
    ) return filteredJobs
    return filteredJobs.filter((j: any) => {
      if (calStatusFilter.length > 0 && !calStatusFilter.includes(j.status)) return false
      // 'unassigned' means no crew AND no vendor — a job dispatched to a
      // vendor is still assigned even if no internal tech is on it. CSR
      // never counts toward the assignment concept (internal coordinator,
      // not a dispatch target).
      if (calAgentFilter === 'unassigned' && (j.assigned_agent_id || j.vendor_id)) return false
      if (calAgentFilter !== 'all' && calAgentFilter !== 'unassigned' && j.assigned_agent_id !== calAgentFilter) return false
      // Derived invoice state: both branches require status='complete'; the
      // invoice_id presence distinguishes "ready" (no invoice yet) from
      // "invoiced" (already converted).
      if (calInvoiceFilter === 'ready' && !(j.status === 'complete' && !j.invoice_id)) return false
      if (calInvoiceFilter === 'invoiced' && !(j.status === 'complete' && !!j.invoice_id)) return false
      return true
    })
  }, [filteredJobs, calStatusFilter, calAgentFilter, calInvoiceFilter])

  const boardJobs = useMemo(() => {
    // 'ready_for_invoice' bypasses the today-only scoping because AR needs to
    // find completed-no-invoice work across all dates, not just today's jobs.
    // All other filters operate on today's board for the standard dispatch
    // workflow. This is a deliberate scope-change coupling on the filter
    // button; the "(all dates)" suffix on the button label makes it legible.
    // The row shape from readyForInvoiceJobs matches the calendar query so
    // downstream JobCard rendering is identical.
    // Pagination banked for future polish if workspaces accumulate thousands
    // of stale unbilled jobs.
    if (statusFilter === 'ready_for_invoice') return readyForInvoiceJobs
    const today = format(new Date(), 'yyyy-MM-dd')
    let list = filteredJobs.filter((j: any) => j.scheduled_date === today)
    if (statusFilter !== 'all') {
      list = list.filter((j: any) => j.status === statusFilter)
    }
    return list
  }, [filteredJobs, statusFilter, readyForInvoiceJobs])

  const headerTitle = useMemo(() => {
    if (calView === 'month') return format(currentDate, 'MMMM yyyy')
    if (calView === 'week') return `${format(startOfWeek(currentDate), 'MMM d')} – ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`
    return format(currentDate, 'EEEE, MMMM d, yyyy')
  }, [calView, currentDate])

  const TABS: { id: Tab; label: string }[] = [
    { id: 'calendar', label: 'Calendar' },
    { id: 'board', label: 'Job board' },
    { id: 'map', label: 'Live map' },
    { id: 'crew', label: 'Crew' },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Header */}
      <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Field Hub</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{metrics.active} active · {metrics.scheduled} scheduled</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: 280, maxWidth: '100%' }}>
              <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search by job ID, customer, address..." aria-label="Search jobs"
                style={{ width: '100%', padding: '9px 32px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
              {searchInput && <button onClick={() => setSearchInput('')} aria-label="Clear search" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
            </div>
            <button onClick={() => handleNewJob()} data-testid="field-hub-new-job-cta" className="btn-primary">
              <Plus style={{ width: 15, height: 15 }} /> New job
            </button>
          </div>
        </div>

        {/* Metrics — auto-fit so cards reflow on narrow viewports instead
            of compressing 4-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'This period', value: metrics.total, color: 'var(--accent)' },
            { label: 'Active now', value: metrics.active, color: 'var(--positive)' },
            { label: 'Scheduled', value: metrics.scheduled, color: 'var(--info)' },
            { label: 'On hold', value: metrics.held, color: 'var(--status-job-customer-hold)' },
            { label: 'Complete', value: metrics.complete, color: 'var(--ink-tertiary)' },
          ].map(m => (
            <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
              <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>{m.label}</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: m.color, margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Tab bar — wraps below the calendar nav on narrow viewports so
            neither row overflows horizontally on mobile. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => switchTab(tab.id)}
                style={{ padding: '7px 14px', borderRadius: 9, fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, cursor: 'pointer', background: activeTab === tab.id ? 'var(--accent-dim)' : 'transparent', color: activeTab === tab.id ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: activeTab === tab.id ? '1px solid var(--accent-border)' : '1px solid transparent', transition: 'all 150ms ease' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'calendar' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {(['month', 'week', 'day'] as const).map(v => (
                  <button key={v} onClick={() => switchView(v)}
                    style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: calView === v ? 600 : 400, cursor: 'pointer', background: calView === v ? 'var(--accent)' : 'var(--hover-bg)', color: calView === v ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: 'none', textTransform: 'capitalize' }}>
                    {v}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => navDate(-1)} style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--field-bg)', border: '1px solid var(--border-default)', cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft style={{ width: 14, height: 14 }} />
                </button>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', minWidth: 180, textAlign: 'center' }}>{headerTitle}</span>
                <button onClick={() => navDate(1)} style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--field-bg)', border: '1px solid var(--border-default)', cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight style={{ width: 14, height: 14 }} />
                </button>
                <button onClick={() => setCurrentDate(new Date())}
                  style={{ padding: '5px 10px', borderRadius: 7, background: 'var(--field-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-secondary)', fontSize: 12, cursor: 'pointer' }}>
                  Today
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Calendar/Map filter row — status pills + employee dropdown.
            Hidden on Board (which has its own statusFilter chain) and Crew.
            Multi-select pills toggle into calStatusFilter; "All" clears.
            "On hold" is a combined pill that toggles all 4 HOLD_STATUSES
            in or out together — it shows as active only when ALL 4 are
            currently selected. */}
        {(activeTab === 'calendar' || activeTab === 'map') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {(() => {
                const allHoldsActive = HOLD_STATUSES.every(h => calStatusFilter.includes(h))
                const togglePlain = (s: string) => setCalStatusFilter(prev =>
                  prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
                )
                const toggleHolds = () => setCalStatusFilter(prev =>
                  allHoldsActive
                    ? prev.filter(x => !HOLD_STATUSES.includes(x as any))
                    : Array.from(new Set([...prev, ...HOLD_STATUSES]))
                )
                const allEmpty = calStatusFilter.length === 0
                const pill = (key: string, label: string, active: boolean, accent: string | null, onClick: () => void) => (
                  <button key={key} onClick={onClick}
                    style={{ padding: '5px 11px', borderRadius: 100, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: active ? (accent ? `color-mix(in srgb, ${accent} var(--status-tint-fill), transparent)` : 'var(--accent-dim)') : 'var(--hover-bg)',
                      color: active ? (accent ?? 'var(--accent-text)') : 'var(--ink-tertiary)',
                      border: active ? `1px solid ${accent ? `color-mix(in srgb, ${accent} var(--status-tint-edge), transparent)` : 'var(--accent-border)'}` : '1px solid var(--border-subtle)' }}>
                    {label}
                  </button>
                )
                return (
                  <>
                    {pill('all', 'All', allEmpty, null, () => setCalStatusFilter([]))}
                    {pill('scheduled', JOB_STATUS_CONFIG.scheduled.label, calStatusFilter.includes('scheduled'), JOB_STATUS_CONFIG.scheduled.color, () => togglePlain('scheduled'))}
                    {pill('en_route', JOB_STATUS_CONFIG.en_route.label, calStatusFilter.includes('en_route'), JOB_STATUS_CONFIG.en_route.color, () => togglePlain('en_route'))}
                    {pill('on_site', JOB_STATUS_CONFIG.on_site.label, calStatusFilter.includes('on_site'), JOB_STATUS_CONFIG.on_site.color, () => togglePlain('on_site'))}
                    {pill('on_hold', 'On hold', allHoldsActive, JOB_STATUS_CONFIG.on_customer_hold.color, toggleHolds)}
                    {pill('complete', JOB_STATUS_CONFIG.complete.label, calStatusFilter.includes('complete'), JOB_STATUS_CONFIG.complete.color, () => togglePlain('complete'))}
                    {pill('cancelled', JOB_STATUS_CONFIG.cancelled.label, calStatusFilter.includes('cancelled'), JOB_STATUS_CONFIG.cancelled.color, () => togglePlain('cancelled'))}
                    {/* Visual divider — the pills past this point are DERIVED
                        filters (computed from job data: assignment presence,
                        invoice presence), not real status values. Keeping
                        them visually separated avoids the implication that
                        they're toggling against the same axis as the status
                        pills above. */}
                    <span style={{ width: 1, height: 18, background: 'var(--border-subtle)', margin: '0 4px' }} aria-hidden />
                    {pill('unassigned', 'Unassigned', calAgentFilter === 'unassigned', 'var(--warning)',
                      () => setCalAgentFilter(prev => prev === 'unassigned' ? 'all' : 'unassigned'))}
                    {pill('ready', 'Ready to invoice', calInvoiceFilter === 'ready', 'var(--accent)',
                      () => setCalInvoiceFilter(prev => prev === 'ready' ? 'all' : 'ready'))}
                    {pill('invoiced', 'Invoiced', calInvoiceFilter === 'invoiced', 'var(--positive)',
                      () => setCalInvoiceFilter(prev => prev === 'invoiced' ? 'all' : 'invoiced'))}
                  </>
                )
              })()}
            </div>
            <select value={calAgentFilter} onChange={e => setCalAgentFilter(e.target.value)} aria-label="Filter by employee"
              style={{ padding: '6px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', cursor: 'pointer', maxWidth: 180 }}>
              <option value="all">All employees</option>
              <option value="unassigned">Unassigned</option>
              {agents.map((a: any) => (
                <option key={a.id} value={a.id}>{a.full_name || 'Unnamed'}</option>
              ))}
            </select>
            {(allJobs.length - calendarJobs.length) > 0 && (
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 500 }}>
                {allJobs.length - calendarJobs.length} hidden
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={isMobile ? { display: 'flex', flexDirection: 'column', paddingTop: 12 } : { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', paddingTop: 12 }}>

        {activeTab === 'calendar' && (
          <div style={isMobile ? { display: 'flex', flexDirection: 'column', padding: '0 12px 12px' } : { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0 28px 28px' }}>
            {/* Needs-scheduling tray — count-gated, collapsible. Sits above
                the calendar grid in the dispatcher-controls zone. When closed
                it's a thin header; when open it shows up to ~180px of
                internally-scrolling job cards so the calendar surface only
                loses a fixed amount of vertical space. Hidden when there are
                zero unscheduled jobs so the chrome stays out of the way. */}
            {unscheduledJobs.length > 0 && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, marginBottom: 12, flexShrink: 0 }}>
                <button onClick={() => setUnscheduledTrayOpen(o => !o)}
                  aria-expanded={unscheduledTrayOpen}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)' }}>
                    <Clock style={{ width: 13, height: 13, color: 'var(--warning)' }} />
                    Needs scheduling
                    <span style={{ padding: '1px 7px', borderRadius: 100, background: 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)', color: 'var(--warning)', fontSize: 11, fontWeight: 700 }}>
                      {unscheduledJobs.length}
                    </span>
                  </span>
                  {unscheduledTrayOpen ? <ChevronUp style={{ width: 14, height: 14, color: 'var(--ink-tertiary)' }} /> : <ChevronDown style={{ width: 14, height: 14, color: 'var(--ink-tertiary)' }} />}
                </button>
                {unscheduledTrayOpen && (
                  <div style={{ padding: '4px 10px 10px', maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {unscheduledJobs.map((job: any) => {
                      const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
                      return (
                        <div key={job.id} onClick={() => setSelectedJob(job)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 10px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 8, cursor: 'pointer' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title}</p>
                            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.contact_name || '—'}</p>
                          </div>
                          {/* Priority chip slots before status — an urgent unscheduled
                              job is the most actionable thing in the app, so it reads
                              left-to-right ahead of the status pill. */}
                          <JobPriorityChip priority={job.priority} size="sm" />
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, flexShrink: 0 }}>{cfg.label}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
            <div style={isMobile ? { background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, display: 'flex', flexDirection: 'column', minHeight: '60vh' } : { flex: 1, overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
              {calView === 'month' && <MonthView currentDate={currentDate} jobs={calendarJobs} onDayClick={(d: string) => setCurrentDate(parseISO(d))} onJobClick={setSelectedJob} onNewJob={handleNewJob} isMobile={isMobile} />}
              {calView === 'week' && <WeekView currentDate={currentDate} jobs={calendarJobs} onJobClick={setSelectedJob} onNewJob={handleNewJob} isMobile={isMobile} />}
              {calView === 'day' && <DayView currentDate={currentDate} jobs={calendarJobs} onJobClick={setSelectedJob} onNewJob={handleNewJob} isMobile={isMobile} />}
            </div>
          </div>
        )}

        {activeTab === 'board' && (
          <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              {['all', 'scheduled', 'en_route', 'on_site', 'complete', 'ready_for_invoice', ...HOLD_STATUSES].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                  {s === 'all' ? 'All today' : s === 'ready_for_invoice' ? 'Ready for invoice' : (JOB_STATUS_CONFIG[s]?.label ?? s.replace(/_/g, ' '))}
                  {s === 'ready_for_invoice' && statusFilter === s && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 500, color: 'var(--ink-secondary)', textTransform: 'none' }}>(all dates)</span>
                  )}
                </button>
              ))}
            </div>
            {boardJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                {search ? (
                  <>
                    <Search style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
                    <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>No jobs match "{search}"</p>
                    <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Try a different search or clear it to see all jobs.</p>
                  </>
                ) : (
                  <>
                    <Truck style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
                    <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>No jobs today</p>
                    <button onClick={() => handleNewJob()} className="btn-primary" style={{ margin: '16px auto 0' }}>
                      <Plus style={{ width: 14, height: 14 }} /> Schedule a job
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {boardJobs.map((job: any) => {
                  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
                  const nextStatus = NEXT_STATUS[job.status]
                  const nextCfg = nextStatus ? JOB_STATUS_CONFIG[nextStatus] : null
                  return (
                    <div key={job.id} data-testid="field-job-row" style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 12, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{job.job_number}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                        {job._hasConflict && <ConflictIndicator size={12} />}
                        {job.series_id && <RecurringIndicator size={12} />}
                      </div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{job.title}</p>
                      <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '0 0 8px' }}>{job.contact_name}</p>
                      {(job.address_city || job.address_state) && (
                        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin style={{ width: 10, height: 10 }} />{[job.address_city, job.address_state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setSelectedJob(job)}
                          style={{ flex: 1, padding: '7px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          View
                        </button>
                        {nextCfg && (
                          <button onClick={() => handleUpdate(job.id, buildStatusTransitionPayload(job.status, nextStatus))}
                            style={{ flex: 2, padding: '7px', background: `color-mix(in srgb, ${nextCfg.color} var(--status-tint-faint), transparent)`, border: `1px solid ${nextCfg.border}`, borderRadius: 8, color: nextCfg.color, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                            → {nextCfg.label}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'map' && (
          <MapView jobs={calendarJobs} onJobClick={setSelectedJob} />
        )}

        {activeTab === 'crew' && (
          <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
            {agents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <User style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>No team members yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Invite team members in Settings to see crew view</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {agents.map((agent: any) => {
                  const agentJobs = allJobs.filter((j: any) => j.assigned_agent_id === agent.id)
                  const activeJob = agentJobs.find((j: any) => ['en_route', 'on_site'].includes(j.status))
                  return (
                    <div key={agent.id} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'var(--accent-ink)' }}>
                          {(agent.full_name || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{agent.full_name || 'Agent'}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: activeJob ? 'var(--positive)' : 'var(--ink-tertiary)', boxShadow: activeJob ? '0 0 6px var(--positive)' : 'none' }} />
                            <span style={{ fontSize: 11, color: activeJob ? 'var(--positive)' : 'var(--ink-tertiary)', fontWeight: 600 }}>
                              {activeJob ? activeJob.status.replace('_', ' ') : 'Available'}
                            </span>
                          </div>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-tertiary)', background: 'var(--hover-bg)', padding: '3px 9px', borderRadius: 100 }}>
                          {agentJobs.length} jobs
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {agentJobs.length === 0 ? (
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '8px 0' }}>No assigned jobs this period</p>
                        ) : agentJobs.map((j: any) => {
                          const cfg = JOB_STATUS_CONFIG[j.status]
                          const hasConflict = conflictMap.has(j.id)
                          return (
                            <div key={j.id} onClick={() => setSelectedJob(j)}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, cursor: 'pointer' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                              {hasConflict && <ConflictIndicator size={11} />}
                              {j.series_id && <RecurringIndicator size={11} />}
                              <div>
                                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{j.title}</p>
                                <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>{j.contact_name}</p>
                              </div>
                              <span style={{ fontSize: 10, fontWeight: 700, color: cfg?.color, padding: '2px 7px', borderRadius: 100, background: cfg?.bg, whiteSpace: 'nowrap' }}>{cfg?.label}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals & Drawers */}
      <AnimatePresence>
        {(showNewModal || editingJob) && (
          <NewJobModal
            onClose={() => { setShowNewModal(false); setEditingJob(null) }}
            onSaved={(saved: any) => {
              queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
              if (saved && selectedJob?.id === saved.id) setSelectedJob(saved)
            }}
            contacts={contacts}
            agents={agents}
            clients={clients}
            locations={locationsList}
            vendors={vendors}
            serviceLines={serviceLinesList}
            defaultDate={editingJob?.scheduled_date || newJobDate}
            existingJob={editingJob}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedJob && (
          <>
            {/* allow-hex: framer-motion animate.opacity owns the alpha; --overlay-scrim's baked-in 0.70 alpha would compound with the 0.4 animation to ~0.28 effective. Solid black + animated opacity preserves the original 40% scrim across all modes. */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} exit={{ opacity: 0 }}
              onClick={closeDrawer}
              style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 140 }} />
            <JobDrawer
              job={selectedJob}
              onClose={closeDrawer}
              onEdit={() => setEditingJob(selectedJob)}
              onUpdate={handleUpdate}
              agents={agents}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
